import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;

export const AppointmentReminderSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [hour, setHour] = useState(17);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const { data } = await axios.get(`${API}/notifications/reminder-settings`);
    setCfg(data);
    setEnabled(data.enabled);
    setHour(data.hour);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await axios.put(`${API}/notifications/reminder-settings`, { enabled, hour });
      setCfg(data);
      toast.success(enabled ? `Automatic reminders on — sent daily at ${hourLabel(hour)} ET` : "Automatic reminders turned off");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save reminder settings");
    } finally {
      setBusy(null);
    }
  };

  const sendNow = async () => {
    setBusy("send");
    try {
      const { data } = await axios.post(`${API}/notifications/schedule-appointment-reminders`);
      if (data.appointments_checked === 0) toast.info("No un-reminded appointments scheduled for tomorrow");
      else if (data.errors.length) toast.warning(`Sent ${data.reminders_sent} reminder(s); ${data.errors.length} error(s): ${data.errors[0]}`);
      else toast.success(`Sent ${data.reminders_sent} reminder email(s) for tomorrow's appointments`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminders");
    } finally {
      setBusy(null);
    }
  };

  const dirty = cfg && (cfg.enabled !== enabled || cfg.hour !== hour);
  const last = cfg?.last_result;

  return (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4" data-testid="appointment-reminder-settings">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-semibold text-blue-900">Automatic Appointment Reminders</p>
            <p className="text-sm text-blue-800">Customers and technicians get a branded reminder email the day before each scheduled appointment.</p>
          </div>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="reminder-enabled-toggle" />
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Label className="text-blue-900">Send time (Eastern)</Label>
          <Select value={String(hour)} onValueChange={(v) => setHour(Number(v))}>
            <SelectTrigger className="bg-white" data-testid="reminder-hour-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={save} disabled={!dirty || !!busy} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="reminder-save-button">
          {busy === "save" ? "Saving…" : "Save"}
        </Button>
        <Button onClick={sendNow} disabled={!!busy} size="sm" variant="outline" className="bg-white" data-testid="reminder-send-now-button">
          <Bell className="w-4 h-4 mr-2" />
          {busy === "send" ? "Sending…" : "Send Tomorrow's Reminders Now"}
        </Button>
      </div>
      <p className="text-xs text-blue-800" data-testid="reminder-last-run">
        Last run: {cfg?.last_run ? new Date(cfg.last_run).toLocaleString() : "never"}
        {last ? ` · ${last.reminders_sent} sent, ${last.errors?.length || 0} error(s) for ${last.target_date} (${last.trigger})` : ""}
      </p>
    </div>
  );
};
